import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export interface ReportFilterState {
  period: string;
  startDate?: string;
  endDate?: string;
  storeIds?: string[];
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/** Mirrors backend periodUtils preset ranges so date-only endpoints receive startDate/endDate. */
function computePresetRange(preset: string): { startDate: Date; endDate: Date } {
  const now = new Date();
  const todayEnd = endOfDay(now);

  switch (preset) {
    case 'last_month': {
      const start = startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const end = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));
      return { startDate: start, endDate: end };
    }
    case 'quarter': {
      const quarter = Math.floor(now.getMonth() / 3);
      const start = startOfDay(new Date(now.getFullYear(), quarter * 3, 1));
      return { startDate: start, endDate: todayEnd };
    }
    case 'year': {
      const start = startOfDay(new Date(now.getFullYear(), 0, 1));
      return { startDate: start, endDate: todayEnd };
    }
    case 'this_month':
    default: {
      const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
      return { startDate: start, endDate: todayEnd };
    }
  }
}

export function resolvePeriodDateRange(
  filter: ReportFilterState
): { startDate: string; endDate: string } | null {
  if (filter.period === 'custom') {
    if (filter.startDate && filter.endDate) {
      return { startDate: filter.startDate, endDate: filter.endDate };
    }
    return null;
  }

  const range = computePresetRange(filter.period);
  return {
    startDate: range.startDate.toISOString(),
    endDate: range.endDate.toISOString()
  };
}

export type ExportDetailLevel = 'summary' | 'detailed';

export interface ExportRequestEvent {
  format: string;
  detailLevel?: ExportDetailLevel;
}

export function applyExportDetailParams(
  params: Record<string, any>,
  detailLevel?: ExportDetailLevel
): Record<string, any> {
  if (detailLevel === 'detailed') {
    params['detail'] = 'lines';
  }
  return params;
}

export function exportDetailFilenameSuffix(detailLevel?: ExportDetailLevel): string {
  return detailLevel === 'detailed' ? '-detailed' : '';
}

export function toApiParams(filter: ReportFilterState, extra: Record<string, any> = {}): Record<string, any> {
  const params: Record<string, any> = { ...extra, period: filter.period };
  if (filter.period === 'custom') {
    const dates = resolvePeriodDateRange(filter);
    if (dates) {
      params['startDate'] = dates.startDate;
      params['endDate'] = dates.endDate;
    }
  }
  if (filter.storeIds?.length) {
    params['storeIds'] = filter.storeIds.join(',');
  }
  return params;
}

@Component({
  selector: 'app-report-filter-bar',
  templateUrl: './report-filter-bar.component.html',
  styleUrls: ['./report-filter-bar.component.scss']
})
export class ReportFilterBarComponent implements OnInit, OnChanges {

  @Input() showStoreFilter: boolean = false;
  @Input() showExportButton: boolean = true;
  @Input() exportFormats: string[] = ['csv', 'xlsx'];
  @Input() stores: { _id: string; name: string }[] = [];
  @Input() disabled: boolean = false;
  @Input() showExportDetailLevel: boolean = false;
  @Input() set filterValue(value: ReportFilterState | undefined) {
    if (value) {
      this.applyFilterState(value, false);
    }
  }

  @Output() filterChange = new EventEmitter<ReportFilterState>();
  @Output() exportRequested = new EventEmitter<ExportRequestEvent>();

  readonly presets = ['this_month', 'last_month', 'quarter', 'year', 'custom'] as const;

  selectedPeriod: string = 'this_month';
  customStartDate: string = '';
  customEndDate: string = '';
  selectedStoreIds: Set<string> = new Set();
  showStorePicker: boolean = false;
  applyingCustom: boolean = false;
  exportDetailLevel: ExportDetailLevel = 'summary';

  constructor(
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initStoreSelection();
    this.emitFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stores'] && this.showStoreFilter) {
      this.initStoreSelection();
      this.emitFilter();
    }
  }

  private initStoreSelection(): void {
    if (this.showStoreFilter && this.stores.length) {
      this.selectedStoreIds = new Set(this.stores.map(s => s._id));
    }
  }

  selectPeriod(period: string): void {
    if (this.disabled) return;
    this.selectedPeriod = period;
    if (period !== 'custom') this.emitFilter();
  }

  applyCustomRange(): void {
    if (this.disabled || this.applyingCustom) return;
    if (!this.customStartDate || !this.customEndDate) {
      this.toastr.warning(this.translate.instant('KPI.FILTER_BAR.DATES_REQUIRED'));
      return;
    }
    if (this.customStartDate > this.customEndDate) {
      this.toastr.warning(this.translate.instant('KPI.FILTER_BAR.INVALID_DATE_RANGE'));
      return;
    }
    this.applyingCustom = true;
    this.emitFilter();
    setTimeout(() => { this.applyingCustom = false; }, 300);
  }

  toggleStore(storeId: string): void {
    if (this.disabled) return;
    if (this.selectedStoreIds.has(storeId)) this.selectedStoreIds.delete(storeId);
    else this.selectedStoreIds.add(storeId);
    this.emitFilter();
  }

  isStoreSelected(storeId: string): boolean {
    return this.selectedStoreIds.has(storeId);
  }

  selectAllStores(): void {
    if (this.disabled) return;
    this.stores.forEach(s => this.selectedStoreIds.add(s._id));
    this.emitFilter();
  }

  clearStoreSelection(): void {
    if (this.disabled) return;
    this.selectedStoreIds.clear();
    this.emitFilter();
  }

  onExport(format: string): void {
    if (this.disabled) return;
    const event: ExportRequestEvent = { format };
    if (this.showExportDetailLevel) {
      event.detailLevel = this.exportDetailLevel;
    }
    this.exportRequested.emit(event);
  }

  setExportDetailLevel(level: ExportDetailLevel): void {
    if (this.disabled) return;
    this.exportDetailLevel = level;
  }

  presetKey(period: string): string {
    return `KPI.FILTER_BAR.${period.toUpperCase()}`;
  }

  private applyFilterState(state: ReportFilterState, emit: boolean): void {
    this.selectedPeriod = state.period;
    if (state.period === 'custom') {
      this.customStartDate = this.toDateInputValue(state.startDate);
      this.customEndDate = this.toDateInputValue(state.endDate);
    } else {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    if (emit) {
      this.emitFilter();
    }
  }

  private toDateInputValue(value?: string): string {
    if (!value) {
      return '';
    }
    return value.length >= 10 ? value.slice(0, 10) : value;
  }

  private emitFilter(): void {
    const state: ReportFilterState = { period: this.selectedPeriod };
    if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
      state.startDate = this.customStartDate;
      state.endDate = this.customEndDate;
    }
    if (this.showStoreFilter && this.selectedStoreIds.size > 0) {
      state.storeIds = Array.from(this.selectedStoreIds);
    }
    this.filterChange.emit(state);
  }
}
